import '../i18n/autoTranslate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AppBackground from '../../components/ui/AppBackground.tsx';
import SpotlightCard from '../../components/ui/SpotlightCard';
import QuickActionButton from '../../components/ui/QuickActionButton';
import { Alert, AlertDescription } from '../../components/ui/shadcn/alert';

import WelcomeBanner from './components/WelcomeBanner';
import WelcomeBannerSkeleton from './components/WelcomeBannerSkeleton';
import MetricCard from './components/MetricCard';
import MetricCardsSkeletonRow from './components/MetricCardsSkeletonRow';
import QuickStartSection from './components/QuickStartSection';
import ActivityFeed from './components/ActivityFeed';
import UpcomingSessions from './components/UpcomingSessions';
import ProgressVisualization from './components/ProgressVisualization';
import NotificationStrip from './components/NotificationStrip';
import AICoachingCard from './components/AICoachingCard';

import { fetchInterviews, fetchInterviewStatistics } from '../../features/interview/interviewThunk';
import {
  selectSuccessMessage,
  selectInterviewError,
  clearError,
  clearSuccessMessage,
} from '../../features/interview/interviewSlice';
import { refreshAccessToken } from '../../features/auth/authThunks';
import { API_BASE_URL, getAccessToken } from '../../config/api';
import SEO from '../SEO.jsx';

// NOTE: bumped version so the strip shows again even if user dismissed an older variant.
const DISMISS_KEY = 'ig_dashboard_notif_dismissed_v2';

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, accessToken, refreshToken, loading: authLoading } = useSelector((s) => s.auth);

  const successMessage = useSelector(selectSuccessMessage);
  const errorInterviews = useSelector(selectInterviewError);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  // NotificationStrip dismiss state lives in the parent (single source of truth).
  const [notifDismissed, setNotifDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Keep token refreshed
  useEffect(() => {
    if (!accessToken && refreshToken && !authLoading) {
      dispatch(refreshAccessToken());
      return;
    }

    if (accessToken && refreshToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();
        if (exp - now < 60_000) dispatch(refreshAccessToken());
      } catch {
        // ignore parsing failures
      }
    }
  }, [accessToken, refreshToken, authLoading, dispatch]);

  // Fetch activities helper
  const fetchActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const token = getAccessToken();
      const res = await fetch(`${API_BASE_URL}/api/interview/activities`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(Array.isArray(data?.activities) ? data.activities : []);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch activities:', e);
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    dispatch(fetchInterviews());
    dispatch(fetchInterviewStatistics());
    fetchActivities();
  }, [dispatch, fetchActivities]);

  // Auto-clear success messages
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => dispatch(clearSuccessMessage()), 3000);
    return () => clearTimeout(t);
  }, [successMessage, dispatch]);

  // Pull-to-refresh — dispatches real thunks (not a fake setTimeout).
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchInterviews()),
        dispatch(fetchInterviewStatistics()),
        fetchActivities(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, fetchActivities]);

  // Derived user data
  const userData = useMemo(() => {
    if (!user) return null;
    return {
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User',
      currentStreak: user.current_streak ?? user.metrics?.current_streak ?? 0,
      level: user.level ?? 1,
      xp: user.xp ?? 0,
      nextLevelXp: user.next_level_xp ?? 0,
      plan: user.plan || 'Free',
      rank: user.rank || 'Unranked',
      metrics: user.metrics || {},
    };
  }, [user]);

  const metricsData = useMemo(() => {
    const m = userData?.metrics || {};
    return [
      {
        title: 'Average Score',
        value: Number(m.average_score) > 0 ? `${m.average_score}%` : 'No scores',
        subtitle:
          Number(m.average_score) > 0 ? 'Last 7 interviews' : 'Start your first interview',
        icon: 'Target',
        trend: m.score_trend || 0,
        color: 'primary',
        onClick: () => navigate('/progress-analytics-insights'),
      },
      {
        title: 'Weekly Progress',
        value:
          Number(m.sessions_completed_this_week) > 0
            ? `${m.sessions_completed_this_week}`
            : 'Start Now',
        subtitle: 'Sessions completed',
        icon: 'TrendingUp',
        trend: m.completion_trend || 0,
        color: 'success',
        onClick: () =>
          navigate(
            Number(m.sessions_completed_this_week) > 0
              ? '/progress-analytics-insights'
              : '/interview-setup-configuration'
          ),
      },
      {
        title: 'Achievement Badges',
        value: Number(m.badges_earned) > 0 ? `${m.badges_earned}` : 'Earn Badges',
        subtitle: Number(m.badges_earned) > 0 ? 'Total earned' : 'Complete challenges',
        icon: 'Award',
        trend: m.badges_trend || 0,
        color: 'accent',
        onClick: () => navigate('/progress-analytics-insights'),
      },
      {
        title: 'Practice Streak',
        value: Number(m.current_streak) > 0 ? `${m.current_streak}` : 'Start Streak',
        subtitle: 'Days in a row',
        icon: 'Flame',
        trend: m.streak_trend || 0,
        color: 'warning',
        onClick: () => navigate('/interview-setup-configuration'),
      },
    ];
  }, [navigate, userData]);

  // Coaching insight (used by NotificationStrip + AICoachingCard)
  const coachingInsight = useMemo(() => {
    const tip = activities.find(
      (a) => a?.type === 'improvement_tip' || a?.type === 'ai_coach_recommendation'
    );
    if (tip?.description) {
      return {
        title: 'AI Coach Recommendation',
        stripMessage:
          'AI Coach: You have a fresh recommendation ready — try a focused session today.',
        description: tip.description,
        tags: ['Behavioral', 'STAR Method', '+50 XP'],
        ctaTo: '/interview-setup-configuration',
      };
    }

    const m = userData?.metrics || {};
    const ps = m.problem_solving_trend;
    const scoreTrend = m.score_trend;
    if (
      (typeof ps === 'number' && ps < -5) ||
      (typeof scoreTrend === 'number' && scoreTrend < -5)
    ) {
      return {
        title: 'AI Coach',
        stripMessage:
          'Your last session shows a drop in problem-solving. Try the "Stress Interview" mode today.',
        description:
          'Based on your recent sessions, your problem-solving signal dipped. A short Stress Interview can help rebuild focus under pressure.',
        tags: ['Stress Interview', 'Problem Solving', '+50 XP'],
        ctaTo: '/interview-setup-configuration',
      };
    }

    // Fallback insight so the new UI always appears even if backend trends/tips are empty.
    return {
      title: 'AI Coach',
      stripMessage:
        'AI Coach: Your last session shows a drop in problem-solving. Try the "Stress Interview" mode today.',
      description:
        'Try a quick Stress Interview to rebuild clarity under pressure. Keep answers structured (Problem → Approach → Result) and avoid over-explaining.',
      tags: ['Stress Interview', 'Problem Solving', 'Structure'],
      ctaTo: '/interview-setup-configuration',
    };
  }, [activities, userData]);

  const showNotificationStrip = Boolean(!notifDismissed && coachingInsight?.stripMessage);

  const dismissNotification = () => {
    setNotifDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    document.title = 'Dashboard - AI Interview Trainer';
  }, []);

  return (
    <AppBackground>
      <div className="mt-[60px] md:mt-0">
      <SEO
  title="Dashboard — InterviewGenieAI"
  description="Manage your AI interviews, view your progress, track scores, and continue your interview preparation inside InterviewGenieAI."
  canonical="https://interviewgenieai.com/dashboard-home"
  noindex={true}
/>
        <main className="relative z-10 pt-6 pb-24 md:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Pull to Refresh Indicator */}
            {isRefreshing && (
              <div className="fixed top-24 left-1/2 -translate-x-1/2 z-40 bg-white/5 border border-white/10 rounded-full px-4 py-2 shadow-lg">
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                  <span>Refreshing...</span>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {/* Contextual Notification Strip */}
              {showNotificationStrip && (
                <NotificationStrip
                  title={coachingInsight.title}
                  message={coachingInsight.stripMessage}
                  onDismiss={dismissNotification}
                  onAction={() =>
                    navigate(coachingInsight.ctaTo || '/interview-setup-configuration')
                  }
                  actionLabel="Try Stress Interview"
                />
              )}

              {/* Success + Error (shadcn Alert) */}
              {successMessage && (
                <Alert className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl">
                  <AlertDescription className="text-white/80">{successMessage}</AlertDescription>
                </Alert>
              )}
              {errorInterviews && (
                <Alert
                  variant="destructive"
                  className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl"
                >
                  <AlertDescription className="text-white/80 flex items-start justify-between gap-3">
                    <span className="min-w-0">{String(errorInterviews)}</span>
                    <button
                      type="button"
                      onClick={() => dispatch(clearError())}
                      className="shrink-0 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80 hover:bg-white/10"
                      aria-label="Dismiss error"
                    >
                      ×
                    </button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Welcome Banner */}
              {!userData ? (
                <WelcomeBannerSkeleton />
              ) : (
                <WelcomeBanner
                  id="welcome-banner"
                  userName={userData.name}
                  currentStreak={userData.currentStreak}
                  level={userData.level}
                  xp={userData.xp}
                  next_level_xp={userData.nextLevelXp}
                  rank={userData.rank}
                  plan={userData.plan}
                />
              )}

              {/* Metric Cards */}
              {!userData ? (
                <MetricCardsSkeletonRow />
              ) : (
                <div id="metrics-grid" className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {metricsData.map((metric, index) => (
                    <MetricCard key={index} {...metric} />
                  ))}
                </div>
              )}

              {/* AI Coaching Insight Card */}
              {coachingInsight?.description && (
                <AICoachingCard
                  title={coachingInsight.title || 'AI Coach Recommendation'}
                  description={coachingInsight.description}
                  tags={coachingInsight.tags || []}
                  ctaLabel="Start Stress Interview"
                  onCta={() =>
                    navigate(coachingInsight.ctaTo || '/interview-setup-configuration')
                  }
                />
              )}

              {/* Quick Start */}
              <QuickStartSection id="quick-start" />

              {/* Main Content Grid */}
              <SpotlightCard
                className="custom-spotlight-card relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.40)] p-6"
                spotlightColor="rgba(139, 92, 246, 0.02)"
              >
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left: Activity + Progress */}
                  <div className="lg:col-span-2 space-y-8">
                    <ActivityFeed activities={activities} loading={loadingActivities} />

                    {/* Render ProgressVisualization ONCE (no duplicate API calls) */}
                    <ProgressVisualization />
                  </div>

                  {/* Right: Sidebar */}
                  <div className="lg:col-span-1">
                    <UpcomingSessions />
                  </div>
                </div>
              </SpotlightCard>
            </div>
          </div>
        </main>

        {/* Quick Action Button */}
        <QuickActionButton
          isVisible={true}
          onQuickStart={() => navigate('/interview-setup-configuration')}
          id="quick-action"
        />

        {/* Mobile Pull to Refresh — listeners scoped + always cleaned up */}
        <div
          className="md:hidden fixed top-16 left-0 right-0 h-16 z-30"
          onTouchStart={(e) => {
            const startY = e?.touches?.[0]?.clientY;
            if (typeof startY !== 'number') return;

            const handleTouchMove = (ev) => {
              const currentY = ev?.touches?.[0]?.clientY;
              if (typeof currentY !== 'number') return;
              if (currentY - startY > 100 && window.scrollY === 0) {
                handleRefresh();
                cleanup();
              }
            };

            const cleanup = () => {
              document.removeEventListener('touchmove', handleTouchMove);
              document.removeEventListener('touchend', cleanup);
              document.removeEventListener('touchcancel', cleanup);
            };

            document.addEventListener('touchmove', handleTouchMove, { passive: true });
            document.addEventListener('touchend', cleanup, { once: true });
            document.addEventListener('touchcancel', cleanup, { once: true });
          }}
        />
      </div>
    </AppBackground>
  );
};

export default DashboardHome;
